import { useEffect, useMemo, useState } from "react";
import { AevoStaking, AevoStaking__factory } from "../../codegen";
import { CONTRACT_ADDRESSES } from "../../constants/addresses";
import { ChainIdEnum } from "../../enums/chain";
import { IWallet } from "../wallet/useWallet";
import { useWeb3Context } from "../../contexts/Web3Context";
import { supportedChainId } from "../../utils/wallet/connectors";

export const getAevoStakingContract = (
  provider?: any,
  chainId?: ChainIdEnum,
  useSigner: boolean = true,
  useNewContract: boolean = false
): AevoStaking | undefined => {
  const signerOrProvider = useSigner ? provider?.getSigner() : provider;
  if (signerOrProvider && chainId) {
    const chainAddresses = CONTRACT_ADDRESSES[chainId];
    const chainAddress = useNewContract
      ? chainAddresses?.aevoStakingNew
      : chainAddresses?.aevoStaking;
    if (chainAddress) {
      return AevoStaking__factory.connect(chainAddress, signerOrProvider);
    }
  }

  return undefined;
};

const useAevoStaking = (wallet: IWallet, useNewContract: boolean = false) => {
  const { provider, chainId } = wallet;
  const { provider: defaultProvider } = useWeb3Context();
  const [aevoStaking, setAevoStaking] = useState<AevoStaking>();
  const { account } = wallet;
  const [isAccountWhitelisted, setIsAccountWhitelisted] =
    useState<boolean>(false);
  const useDefaultProvider = useMemo(
    () => chainId !== supportedChainId,
    [chainId]
  );

  useEffect(() => {
    setAevoStaking(
      getAevoStakingContract(
        useDefaultProvider ? defaultProvider : provider,
        supportedChainId, // only ethereum
        !useDefaultProvider,
        useNewContract
      )
    );
  }, [chainId, defaultProvider, provider, useDefaultProvider, useNewContract]);

  useEffect(() => {
    const fetchIsWhitelisted = async () => {
      if (!account || !aevoStaking || !useNewContract) return;

      try {
        // Retrieve eth balance
        const isWhitelisted = await aevoStaking.isWhitelisted(account);
        setIsAccountWhitelisted(isWhitelisted);
      } catch (err) {
        console.error("Error fetching balances:", {
          error: err,
        });
      }
    };

    fetchIsWhitelisted();
  }, [account, aevoStaking, useNewContract]);

  return {
    aevoStaking,
    isAccountWhitelisted,
    decimals: 18,
  };
};

export default useAevoStaking;
